import React from "react";
import { Lightbulb, Quote } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocaleFormat } from "../i18n/useLocaleFormat";

/**
 * "Pourquoi cette alerte" section — decision-business explanation.
 * - Translates `rationale.reasons[]` codes to FR/EN one-liners
 * - Surfaces `evidence_quote` VERBATIM from the source document (never translated)
 * - Renders `impact_breakdown` with locale-aware currency formatting
 */
export default function WhyThisAlert({ alert }) {
  const { t } = useTranslation("alerts");
  const { formatCurrency } = useLocaleFormat();
  const r = alert?.rationale;
  if (!r || (!r.reasons?.length && !r.evidence_quote && !r.impact_breakdown)) return null;

  return (
    <section
      data-testid="why-this-alert"
      className="bg-blue-50/60 border border-blue-200 rounded-lg p-4 my-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb size={14} className="text-blue-700" strokeWidth={2} />
        <h4 className="font-display font-semibold text-sm text-blue-950 tracking-tight">
          {t("why.title")}
        </h4>
      </div>

      {r.reasons?.length > 0 && (
        <ul className="space-y-1.5 mb-3" data-testid="why-reasons-list">
          {r.reasons.map((code) => (
            <li
              key={code}
              data-testid={`why-reason-${code}`}
              className="text-sm text-zinc-800 leading-snug flex items-start gap-1.5"
            >
              <span className="text-blue-600 mt-0.5">·</span>
              <span>{t(`why.reasons.${code}`, code)}</span>
            </li>
          ))}
        </ul>
      )}

      {r.impact_breakdown?.amount > 0 && (
        <div
          data-testid="why-impact-breakdown"
          className="mt-3 pt-3 border-t border-blue-100 flex items-baseline gap-2"
        >
          <span className="text-[11px] uppercase tracking-wider text-blue-700 font-semibold">
            {t("why.impact_label")}
          </span>
          <span className="font-display font-bold text-lg text-zinc-950 tabular-nums">
            {formatCurrency(r.impact_breakdown.amount, r.impact_breakdown.currency)}
          </span>
        </div>
      )}

      {r.evidence_quote && (
        <div
          data-testid="why-evidence"
          className="mt-3 pt-3 border-t border-blue-100"
        >
          <div className="flex items-center gap-1.5 mb-1.5">
            <Quote size={10} className="text-blue-600" />
            <span className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">
              {t("why.evidence_label")}
            </span>
          </div>
          <p
            className="text-[12px] font-mono text-zinc-800 leading-snug whitespace-pre-wrap"
            data-testid="why-evidence-quote"
          >
            &quot;{r.evidence_quote}&quot;
          </p>
          <p className="text-[10px] text-zinc-500 mt-1 italic">
            {t("why.evidence_note")}
          </p>
        </div>
      )}

      {r.recommended_action_key && (
        <p
          data-testid="why-recommended-action"
          className="mt-3 pt-3 border-t border-blue-100 text-xs text-blue-900"
        >
          <span className="font-semibold">{t("why.guardian_recommends")}</span>{" "}
          {t(r.recommended_action_key, t("alerts:action_keys.review_now"))}
        </p>
      )}
    </section>
  );
}
